import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getLanguageDisplay } from '@supply-mover-2-0/core';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { usePortalAuth } from '../../hooks/usePortalAuth';
import * as portalLanguageService from '../../services/portalLanguageService';
import { usePortalAuthStore } from '../../stores/portalAuthStore';
import { LanguageMark } from './LanguageMark';

interface ActiveLanguage {
	id: number;
	code: string;
	name: string;
}

/**
 * Header language switcher — compact button with globe icon + 2-letter code.
 *
 * Fetches active languages once mounted. Renders nothing until languages are
 * loaded or if the tenant only has one active language (no need to switch).
 *
 * Works signed OUT as well (PD-15030): the language endpoints accept
 * anonymous requests, and the sign-in page is exactly where a non-English
 * speaker needs to be able to pick their language. The choice is kept
 * locally; server-side persistence to the user's record only happens when
 * signed in.
 */
export function LanguageSwitcher(): JSX.Element | null {
	const { t, refreshTranslations, currentLanguage } = useTranslation();
	const { token } = usePortalAuth();
	const [isOpen, setIsOpen] = useState(false);
	// Seed from the session cache so remounts (the menu drawer unmounts
	// this component on every close) render the flag on first paint
	// instead of popping it in after a refetch (PD-14607).
	const cached = portalLanguageService.getActiveLanguagesCached();
	const [languages, setLanguages] = useState<Array<ActiveLanguage>>(
		cached ?? [],
	);
	const [loaded, setLoaded] = useState(cached != null);
	const popoverRef = useRef<HTMLDivElement>(null);

	// Load active languages once per session (service-level cache). If
	// there's only one active language the component renders null below.
	// Anonymous-capable — a null token is a legitimate signed-out request.
	useEffect(() => {
		if (loaded) return;
		portalLanguageService
			.getActiveLanguages(token)
			.then((langs) => {
				setLanguages(langs);
				setLoaded(true);
			})
			.catch(() => {
				setLoaded(true);
			});
	}, [token, loaded]);

	// Close popover on outside click
	useEffect(() => {
		if (!isOpen) return;
		const handler = (e: MouseEvent) => {
			if (
				popoverRef.current != null &&
				!popoverRef.current.contains(e.target as Node)
			) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, [isOpen]);

	const handleChange = useCallback(
		async (code: string) => {
			const state = usePortalAuthStore.getState();
			state.setLanguagePreference(code);
			state.persist();

			// Server-side persistence writes to the signed-in user's record;
			// signed out, the local preference above is the whole story.
			if (token != null) {
				portalLanguageService
					.updateLanguagePreference(token, code)
					.catch(() => {
						// Best-effort server persistence — localStorage already updated
					});
			}

			await refreshTranslations();
			setIsOpen(false);
		},
		[token, refreshTranslations],
	);

	// Hide until loaded, or when the tenant only has one language.
	if (!loaded || languages.length <= 1) {
		return null;
	}

	const currentLanguageCode = currentLanguage ?? 'en';

	return (
		<div className="relative" ref={popoverRef}>
			<button
				type="button"
				onClick={() => setIsOpen((v) => !v)}
				className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium text-brand-primary-fg hover:bg-brand-primary-fg/10 transition-colors min-h-touch"
				aria-label={t('Change language')}
				aria-expanded={isOpen}
			>
				{/* Show the active language as a country flag instead of a
				    generic globe — at-a-glance which language is set, even
				    on the mobile drawer. No code beside it (owner decision
				    2026-09-30): the flag and the names in the menu are enough. */}
				<LanguageMark code={currentLanguageCode} className="h-4 w-6" />
			</button>

			{isOpen && (
				<div className="absolute right-0 top-full mt-1 z-50 w-48 bg-brand-surface border border-brand-border rounded-lg shadow-lg overflow-hidden">
					<div className="max-h-64 overflow-y-auto py-1">
						{languages.map((lang) => {
							// PD-16672 — each language is named in its own language
							// (English, 简体中文, 日本語) so a reader finds it on a
							// page they cannot read yet.
							const display = getLanguageDisplay(lang);

							return (
								<button
									key={lang.id}
									type="button"
									onClick={() => void handleChange(lang.code)}
									className="w-full text-left px-3 py-2 text-sm text-strong hover:bg-brand-input flex items-center gap-2 min-h-touch"
								>
									<span className="w-4 text-center text-xs">
										{currentLanguage === lang.code && (
											<FontAwesomeIcon
												icon={faCheck}
												className="text-portal-accent"
											/>
										)}
									</span>
									<LanguageMark
										code={lang.code}
										name={lang.name}
										className="h-3.5 w-5"
									/>
									<span className="truncate" lang={lang.code}>
										{t(display.nativeName)}
									</span>
								</button>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
